/**
 * The Supabase client.
 *
 * Two things are deliberately true here:
 *
 *  - **Only the anon key is present.** It is designed to be public. Every rule that matters is enforced by
 *    RLS and by the `api.*` command functions, so a client holding this key can still only read what its
 *    grants allow. A service-role key must never reach this application (BRIEF §20).
 *  - **Only the `api` schema exists as far as this app is concerned.** PostgREST exposes nothing else, so
 *    there is no way for a component to reach a domain table even by mistake.
 */
import { createClient } from '@supabase/supabase-js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const isConfigured = Boolean(url && anonKey);

/**
 * When the environment is not configured the app still runs: it says so plainly rather than rendering
 * invented data. Every query surfaces the misconfiguration instead of silently returning nothing.
 */
export const supabase = createClient(url ?? 'http://127.0.0.1:54321', anonKey ?? 'not-configured', {
  // detectSessionInUrl is what turns an invitation or recovery link into a session: GoTrue sends the
  // person back here with a token in the URL fragment, and the client exchanges it on load.
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
  db: { schema: 'api' },
  global: { headers: { 'x-application': 'rtcom-erp' } },
});

export class NotConfiguredError extends Error {
  constructor() {
    super(
      'This build has no Supabase connection. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY ' +
        '(see apps/erp/.env.example) and reload.',
    );
    this.name = 'NotConfiguredError';
  }
}

export function assertConfigured(): void {
  if (!isConfigured) throw new NotConfiguredError();
}

/** Calls an `api.*` function, turning a PostgREST error into the ERP's named error. */
export async function rpc<T>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  assertConfigured();
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw toRtcError(error);
  return data as T;
}

export const SESSION_EXPIRED = 'RTC_SESSION_EXPIRED';
export const SESSION_REVOKED = 'RTC_SESSION_REVOKED';

/** True for an expired or invalid session, whether it surfaced through `rpc` or a raw PostgREST read. */
export function isSessionExpired(error: unknown): boolean {
  const e = error as { code?: string; message?: string } | null;
  // RTC_SESSION_REVOKED: an administrator (or the person, from another device) ended this session. The server
  // already treats it as nobody; the browser should stop pretending otherwise.
  return e?.code === SESSION_EXPIRED || e?.code === SESSION_REVOKED || e?.code === 'PGRST301' || e?.code === 'PGRST303' || /JWT expired/i.test(e?.message ?? '');
}

export interface RtcError extends Error {
  code: string;
  details?: string | undefined;
}

/**
 * The database raises named `RTC_*` refusals. They are meanings, not noise: `RTC_UNDECIDED_POLICY` in
 * particular tells the user that the client has not decided a rule — which is a different thing from a bug,
 * and must be shown as such rather than as "something went wrong".
 */
export function toRtcError(error: { message: string; details?: string | null; code?: string }): RtcError {
  const named = /RTC_[A-Z_]+/.exec(error.message)?.[0];
  const rtc = new Error(error.message) as RtcError;
  rtc.name = 'RtcError';
  // PGRST301/PGRST303 are PostgREST's "this token is expired or invalid". Named, so one place can react.
  const expired = error.code === 'PGRST301' || error.code === 'PGRST303' || /JWT expired/i.test(error.message);
  rtc.code = named ?? (expired ? SESSION_EXPIRED : error.code) ?? 'RTC_INTERNAL';
  rtc.details = error.details ?? undefined;
  return rtc;
}

export function isUndecidedPolicy(error: unknown): boolean {
  return (error as RtcError | undefined)?.code === 'RTC_UNDECIDED_POLICY';
}
